import camelcaseKeys from 'camelcase-keys';
import { defineStore } from 'pinia';
import ContactAPI from 'dashboard/api/contacts';

const createEmptyContactState = () => ({
  messages: [],
  cursor: null,
  hasMore: true,
  hasLoaded: false,
  error: false,
});

// Backs the unified read-only timeline in the ContactPanel (FEAT-018).
// Keyed by contactId: once a contact's initial page has loaded, `hasLoaded`
// lets the component skip refetching on remount (e.g. collapsing/reopening
// the accordion) or when navigating back to an already-viewed contact.
// `requestToken` + `activeContactId` (same guard as
// dashboard/stores/companies.js) drop a response that arrives after the
// agent already moved on to a different contact.
// Strips empty/nil filter values so the API never receives e.g. `inbox_id:
// null` — an absent key and an explicit null mean the same thing to the
// controller, but only the former keeps request logs/URLs readable.
const compactFilters = filters =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const useContactTimelineStore = defineStore('contactTimeline', {
  state: () => ({
    activeContactId: null,
    byContactId: {},
    // FEAT-020: search/filter results inside the ContactTimelineDialog live
    // in their own per-contact slot, keyed the same way as `byContactId` —
    // kept separate on purpose so filtering never overwrites the unfiltered
    // preview the accordion caches (see BUG-049-adjacent note in
    // ContactTimeline.vue about hasLoaded meaning "the *preview* is loaded").
    filteredByContactId: {},
    uiFlags: { isFetching: false, isFetchingMore: false },
    requestToken: 0,
  }),

  getters: {
    getMessages: state => contactId =>
      state.byContactId[Number(contactId)]?.messages || [],
    getHasMore: state => contactId =>
      state.byContactId[Number(contactId)]?.hasMore ?? true,
    getHasLoaded: state => contactId =>
      state.byContactId[Number(contactId)]?.hasLoaded ?? false,
    getHasError: state => contactId =>
      state.byContactId[Number(contactId)]?.error ?? false,

    getFilteredMessages: state => contactId =>
      state.filteredByContactId[Number(contactId)]?.messages || [],
    getFilteredHasMore: state => contactId =>
      state.filteredByContactId[Number(contactId)]?.hasMore ?? true,
    getFilteredError: state => contactId =>
      state.filteredByContactId[Number(contactId)]?.error ?? false,
  },

  actions: {
    async fetchInitial(contactId) {
      const id = Number(contactId);
      this.activeContactId = id;
      this.byContactId[id] = createEmptyContactState();
      this.uiFlags.isFetching = true;
      this.requestToken += 1;
      const requestToken = this.requestToken;

      try {
        const { data } = await ContactAPI.getMessages(id, {});
        if (this.requestToken !== requestToken || this.activeContactId !== id) {
          return this.byContactId[id]?.messages || [];
        }
        const messages = camelcaseKeys(data.payload || [], { deep: true });
        this.byContactId[id] = {
          messages,
          cursor: messages.length ? messages[messages.length - 1].id : null,
          hasMore: !!data.has_more,
          hasLoaded: true,
          error: false,
        };
        return messages;
      } catch (error) {
        if (this.requestToken === requestToken && this.activeContactId === id) {
          this.byContactId[id] = { ...createEmptyContactState(), error: true };
        }
        // eslint-disable-next-line no-console
        console.error(
          `[contactTimeline] failed to load messages for contact ${id}`,
          error
        );
        return [];
      } finally {
        if (this.requestToken === requestToken) {
          this.uiFlags.isFetching = false;
        }
      }
    },

    async fetchMore(contactId) {
      const id = Number(contactId);
      const current = this.byContactId[id] || createEmptyContactState();
      if (!current.hasMore || !current.cursor) return current.messages;

      this.uiFlags.isFetchingMore = true;
      this.requestToken += 1;
      const requestToken = this.requestToken;

      try {
        const { data } = await ContactAPI.getMessages(id, {
          before: current.cursor,
        });
        if (this.requestToken !== requestToken || this.activeContactId !== id) {
          return this.byContactId[id]?.messages || [];
        }
        const olderMessages = camelcaseKeys(data.payload || [], { deep: true });
        const messages = [...current.messages, ...olderMessages];
        this.byContactId[id] = {
          ...current,
          messages,
          cursor: olderMessages.length
            ? olderMessages[olderMessages.length - 1].id
            : current.cursor,
          hasMore: !!data.has_more,
        };
        return messages;
      } catch (error) {
        // Network error or server error during paginated load. Don't wipe
        // the state — the user already has real, previously-loaded messages
        // visible on screen and a valid cursor; just let them click
        // "load more" again.
        return current.messages;
      } finally {
        if (this.requestToken === requestToken) {
          this.uiFlags.isFetchingMore = false;
        }
      }
    },

    // FEAT-020: always fetches fresh (no hasLoaded short-circuit) — called
    // every time the agent changes a filter in the dialog, including
    // clearing back to an empty query.
    async searchFiltered(contactId, filters = {}) {
      const id = Number(contactId);
      const cleanFilters = compactFilters(filters);
      this.requestToken += 1;
      const requestToken = this.requestToken;

      try {
        const { data } = await ContactAPI.getMessages(id, cleanFilters);
        if (this.requestToken !== requestToken) {
          return this.filteredByContactId[id]?.messages || [];
        }
        const messages = camelcaseKeys(data.payload || [], { deep: true });
        this.filteredByContactId[id] = {
          messages,
          cursor: messages.length ? messages[messages.length - 1].id : null,
          hasMore: !!data.has_more,
          filters: cleanFilters,
          error: false,
        };
        return messages;
      } catch (error) {
        if (this.requestToken === requestToken) {
          this.filteredByContactId[id] = {
            messages: [],
            cursor: null,
            hasMore: false,
            filters: cleanFilters,
            error: true,
          };
        }
        // eslint-disable-next-line no-console
        console.error(
          `[contactTimeline] failed to search messages for contact ${id}`,
          error
        );
        return [];
      }
    },

    async fetchMoreFiltered(contactId) {
      const id = Number(contactId);
      const current = this.filteredByContactId[id];
      if (!current || !current.hasMore || !current.cursor) {
        return current?.messages || [];
      }

      this.requestToken += 1;
      const requestToken = this.requestToken;

      try {
        const { data } = await ContactAPI.getMessages(id, {
          ...current.filters,
          before: current.cursor,
        });
        if (this.requestToken !== requestToken) {
          return this.filteredByContactId[id]?.messages || [];
        }
        const olderMessages = camelcaseKeys(data.payload || [], { deep: true });
        const messages = [...current.messages, ...olderMessages];
        this.filteredByContactId[id] = {
          ...current,
          messages,
          cursor: olderMessages.length
            ? olderMessages[olderMessages.length - 1].id
            : current.cursor,
          hasMore: !!data.has_more,
        };
        return messages;
      } catch (error) {
        return current.messages;
      }
    },
  },
});
